// src/pages/SocialMediaAccounts/SocialMediaAccounts.jsx

import React, { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import {
  MDBContainer, MDBCard, MDBCardHeader, MDBCardBody,
  MDBTable, MDBTableHead, MDBTableBody, MDBTooltip, MDBBtn
} from 'mdbreact';
import { useDispatch, useSelector } from 'react-redux';

import { useLoadSocialAccounts } from '../../hooks/useLoadsSocialAccounts';

import {
  startConnect,
  doDisconnect,
  applyConnectPayload,
  setSocialMessage,
} from 'actions/social';

import addNotification, { toMessageString } from 'helpers/notify';
import { url as APP_BASE_URL } from 'api';
import {
  shouldShowStatusReason,
  statusReasonTooltip,
  connectButtonFor,
  pickStatusReason,
} from './statusDisplay';
import { getTargetSourceUrl } from 'helpers/socialConnectCTX';

const accountsMeta = [
  // Single LinkedIn card. The Connect handler chains into a second OAuth
  // popup ('linkedin-org') after personal auth succeeds, because LinkedIn
  // forces Community Mgmt API onto a separate app (separate client_id).
  // User sees one button, two consent screens, and both personal + page
  // targets appear in the same connected state.
  { platform: 'linkedin',  name: 'LinkedIn',  color: 'primary',   icon: 'fab fa-linkedin'  },
  { platform: 'twitter',   name: 'Twitter',   color: 'info',      icon: 'fab fa-twitter'   },
  { platform: 'facebook',  name: 'Facebook',  color: 'primary',   icon: 'fab fa-facebook', hasHelp: true },
  { platform: 'pinterest', name: 'Pinterest', color: 'danger',    icon: 'fab fa-pinterest' },
  { platform: 'youtube',   name: 'YouTube',   color: 'danger',    icon: 'fab fa-youtube'   },
  { platform: 'instagram', name: 'Instagram', color: 'danger',    icon: 'fab fa-instagram' },
  { platform: 'snapchat',  name: 'Snapchat',  color: 'warning',   icon: 'fab fa-snapchat'  },
  { platform: 'google',    name: 'Google',    color: 'success',   icon: 'fab fa-google'    },
  { platform: 'tiktok',    name: 'TikTok',    color: 'secondary', icon: 'fab fa-tiktok'    },
];

function notify(msg, type = 'info') {
  try { typeof addNotification === 'function' ? addNotification(msg, type) : console.log(msg); }
  catch (_) { type === 'danger' ? console.error(msg) : console.log(msg); }
}

/* ---------- Status helpers & bridges ---------- */
const getAccountStatus = (acc) => {
  if (!acc) return 'disconnected';
  if (acc.status) return String(acc.status);
  if (acc.expiresAt) {
    try { return new Date(acc.expiresAt) > new Date() ? 'connected' : 'expired'; }
    catch (_) {}
  }
  const looksConnected =
    !!acc.accessToken ||
    !!acc.refreshToken ||
    (Array.isArray(acc.pages) && acc.pages.length > 0);
  return looksConnected ? 'connected' : 'disconnected';
};

const pickBestStatus = (list) => {
  const statuses = (list || []).map(getAccountStatus);
  if (statuses.indexOf('connected') !== -1) return 'connected';
  if (statuses.indexOf('expired')   !== -1) return 'expired';
  if (statuses.indexOf('failed')    !== -1) return 'failed';
  return 'disconnected';
};

function firstSelected(arr) {
  if (!Array.isArray(arr)) return null;
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] && arr[i].selected) return arr[i];
  }
  return null;
}

/* ---------- Build rows per platform from your exact schema ---------- */
// ⬇️ wiring change: accept selectedBrandId & effectiveBrandSettingsId and depend on them
// pickedByPlatform — a SMA-local React state map { facebook: { targetId, targetName, adsId, adsName } }
// populated synchronously by the postMessage CONNECTION_SAVED handler.
// This intentionally bypasses the social-draft slice mismatch (the
// SMC popup writes drafts[<realBsId>] while the parent SMA reads
// drafts['new']) — when present, it wins over everything else.
const usePlatformRows = (
  brandSettings,
  draftsByPlatform = {},
  globalConnections = {},
  selectedBrandId = '',
  effectiveBrandSettingsId = null,
  debugKey = 'SMA',
  pickedByPlatform = {}
) => {
  return useMemo(() => {
    const tag = `[${debugKey}] usePlatformRows`;
    if (brandSettings) {
      console.groupCollapsed(tag);
      console.log('Incoming brandSettings:', {
        byPlatform: brandSettings?.byPlatform,
        selectedByPlatform: brandSettings?.selectedByPlatform,
        socialAccountsLen: (brandSettings?.socialAccounts || []).length,
        socialTargetsLen: (brandSettings?.socialTargets || []).length,
        adsAccountsLen: (brandSettings?.adsAccounts || []).length,
      });
    }

    const rows = {};
    const bs = brandSettings || {};
    let byPlatform = bs.byPlatform || {};
    const selectedMap = bs.selectedByPlatform || {};
    const topAccounts = Array.isArray(bs.socialAccounts) ? bs.socialAccounts : [];

    // If server didn't give byPlatform but gave flats, synthesize a minimal view here (defensive)
    if (!byPlatform || Object.keys(byPlatform).length === 0) {
      byPlatform = {};
      (bs.socialAccounts || []).forEach(a => {
        const p = String(a.platform || '').toLowerCase();
        if (!p) return;
        if (!byPlatform[p]) byPlatform[p] = { accounts: [], targets: [], adsAccounts: [], selected: {} };
        byPlatform[p].accounts.push(a);
      });
      (bs.socialTargets || []).forEach(t => {
        const p = String(t.platform || '').toLowerCase();
        if (!p) return;
        if (!byPlatform[p]) byPlatform[p] = { accounts: [], targets: [], adsAccounts: [], selected: {} };
        byPlatform[p].targets.push(t);
      });
      (bs.adsAccounts || []).forEach(ad => {
        const p = String(ad.platform || '').toLowerCase();
        if (!p) return;
        if (!byPlatform[p]) byPlatform[p] = { accounts: [], targets: [], adsAccounts: [], selected: {} };
        byPlatform[p].adsAccounts.push(ad);
      });
      console.log(`${tag}: synthesized byPlatform from top-level arrays`, byPlatform);
    }

    // Collect every platform we might care about
    const platformsUnion = {};
    // from byPlatform
    Object.keys(byPlatform || {}).forEach((p) => { platformsUnion[p] = true; });
    // from top-level socialAccounts
    for (let i = 0; i < topAccounts.length; i++) {
      const pa = topAccounts[i] && topAccounts[i].platform ? String(topAccounts[i].platform).toLowerCase() : '';
      if (pa) platformsUnion[pa] = true;
    }
    // also ensure the ones we list in UI appear
    for (let m = 0; m < accountsMeta.length; m++) {
      platformsUnion[accountsMeta[m].platform] = platformsUnion[accountsMeta[m].platform] || false;
    }

    Object.keys(platformsUnion).forEach((plat) => {
      let bp = byPlatform[plat] || {};
      let bpAccounts = Array.isArray(bp.accounts) ? bp.accounts : [];
      let bpTargets  = Array.isArray(bp.targets) ? bp.targets : [];
      let bpAds      = Array.isArray(bp.adsAccounts) ? bp.adsAccounts : [];

      // Fallback to top-level arrays filtered by platform
      if (bpAccounts.length === 0) {
        bpAccounts = (bs.socialAccounts || []).filter((a) => String(a?.platform || '').toLowerCase() === plat);
      }
      if (bpTargets.length === 0) {
        bpTargets = (bs.socialTargets || []).filter((t) => String(t?.platform || '').toLowerCase() === plat);
      }
      if (bpAds.length === 0) {
        bpAds = (bs.adsAccounts || []).filter((a) => String(a?.platform || '').toLowerCase() === plat);
      }

      // Resolve selection — priority:
      // 1) selectedByPlatform[plat]
      // 2) any doc with selected=true
      // 3) bridge from selected account's defaultTarget.id -> targets.externalId / ads.externalId
      // 4) first item
      const selFromMap = selectedMap[plat] || {};
      let selAccountId = selFromMap.accountId || null;
      let selTargetId  = selFromMap.targetId  || null;
      let selAdsId     = selFromMap.adsId || selFromMap.adsAccountId || null;

      // (2) selected flags
      if (!selAccountId) {
        const pickedAcc = firstSelected(bpAccounts);
        if (pickedAcc && pickedAcc._id) selAccountId = String(pickedAcc._id);
      }
      if (!selTargetId) {
        const pickedT = firstSelected(bpTargets);
        if (pickedT && pickedT._id) selTargetId = String(pickedT._id);
      }
      if (!selAdsId) {
        const pickedAds = firstSelected(bpAds);
        if (pickedAds && pickedAds._id) selAdsId = String(pickedAds._id);
      }

      // (3) bridge from account.defaultTarget (external id mapping)
      let selectedAccountDoc = null;
      if (selAccountId) {
        for (let j = 0; j < bpAccounts.length; j++) {
          if (String(bpAccounts[j]?._id) === String(selAccountId)) {
            selectedAccountDoc = bpAccounts[j];
            break;
          }
        }
      } else if (bpAccounts.length > 0) {
        selectedAccountDoc = bpAccounts[0];
        selAccountId = String(selectedAccountDoc._id || '');
      }

      // If we still don't have a target, use selectedAccountDoc.defaultTarget.id -> match targets.externalId
      if (!selTargetId && selectedAccountDoc?.defaultTarget?.id) {
        const extId = String(selectedAccountDoc.defaultTarget.id);
        for (let k = 0; k < bpTargets.length; k++) {
          const t = bpTargets[k];
          if (String(t?.externalId) === extId) {
            selTargetId = String(t._id || '');
            break;
          }
        }
      }

      // If we still don't have ads, use selectedAccountDoc.defaultTarget.adsAccountId -> match ads.externalId
      if (!selAdsId && selectedAccountDoc?.defaultTarget?.adsAccountId) {
        const adsExt = String(selectedAccountDoc.defaultTarget.adsAccountId);
        for (let h = 0; h < bpAds.length; h++) {
          const ad = bpAds[h];
          if (String(ad?.externalId) === adsExt) {
            selAdsId = String(ad._id || '');
            break;
          }
        }
      }

      // Ensure final fallbacks to firsts (for Manage button visibility)
      if (!selTargetId && bpTargets.length > 0) selTargetId = String(bpTargets[0]._id || '');
      if (!selAdsId && bpAds.length > 0) selAdsId = String(bpAds[0]._id || '');

      // Human names for subtitle. Match the chosen target by EITHER
      // Mongo `_id` OR external id — optimistic dispatches from the
      // SocialMediaConnect dropdown may only know the external id
      // until the real save round-trips and the SocialTarget doc lands
      // in Redux. Without the externalId branch the row's "Target: X"
      // text would stay stale until the save completed.
      let targetName = null;
      let targetType = null;
      let targetExternalId = null;
      if (selTargetId) {
        const _selStr = String(selTargetId);
        for (let tIdx = 0; tIdx < bpTargets.length; tIdx++) {
          const _t = bpTargets[tIdx];
          if (String(_t?._id) === _selStr || String(_t?.externalId) === _selStr) {
            targetName = _t.name || _t.externalId || null;
            targetType = _t.type || _t.targetType || null;
            targetExternalId = _t.externalId || _t.targetId || null;
            break;
          }
        }
      }
      // Final fallback: if we still don't have a name but the
      // selected account has an optimistically-updated defaultTarget,
      // surface that. SET_BRAND_DEFAULT_TARGET writes defaultTarget.name
      // even when the SocialTarget doc isn't loaded yet.
      if (!targetName && selectedAccountDoc?.defaultTarget?.name) {
        targetName = selectedAccountDoc.defaultTarget.name;
      }
      // Fallback type + externalId from the selected account's defaultTarget.
      if (!targetType && selectedAccountDoc?.defaultTarget?.type) {
        targetType = selectedAccountDoc.defaultTarget.type;
      }
      if (!targetExternalId && selectedAccountDoc?.defaultTarget?.id) {
        targetExternalId = selectedAccountDoc.defaultTarget.id;
      }
      let adsName = null;
      if (selAdsId) {
        for (let aIdx = 0; aIdx < bpAds.length; aIdx++) {
          if (String(bpAds[aIdx]?._id) === String(selAdsId)) {
            adsName = bpAds[aIdx].name || bpAds[aIdx].externalId || null;
            break;
          }
        }
      }

      // Compute status
      let status = 'disconnected';
      if (selectedAccountDoc) status = getAccountStatus(selectedAccountDoc);
      else status = pickBestStatus(bpAccounts);

      // Why-it-broke reason set by the worker when a token/connection
      // issue flips the account to 'failed'. Surfaced in the (?) tooltip.
      const statusReason = pickStatusReason(selectedAccountDoc, bpAccounts);

      rows[plat] = {
        platform: plat,
        accounts: bpAccounts,
        targets: bpTargets,
        ads: bpAds,
        selected: {
          accountId: selAccountId || null,
          targetId: selTargetId || null,
          adsId: selAdsId || null,
          targetName: targetName,
          adsName: adsName,
          targetType: targetType || null,
          targetExternalId: targetExternalId || null,
          // Account username (e.g. Pinterest handle) — needed to build the
          // profile URL for platforms whose target id isn't the profile.
          socialHandle: selectedAccountDoc?.socialHandle || null,
        },
        status: status,
        statusReason: statusReason,
        hasAny: (bpAccounts && bpAccounts.length > 0) || false
      };

      // globalConnections is a brand-agnostic platform-keyed slice
      // (state.social.connections[platform]). It correctly carries
      // status='connected' from the SOCIAL_CONNECT_PAYLOAD dispatched
      // by the OAuth callback in CREATE mode — so we need it there.
      // In EDIT mode it leaks across brands (last brand's "connected"
      // status leaks into this brand's row), so we exclude it.
      // debugKey is the create/edit signal — see how usePlatformRows
      // is called below.
      const overlay = draftsByPlatform[plat] ||
        (debugKey === 'SMA-create' ? globalConnections[plat] : null) ||
        null;

      if (overlay) {
        // 1) status wins from overlay if present
        if (overlay.status) {
          rows[plat].status = overlay.status;
        }

        // 2) Selection from the overlay ALWAYS wins over bsFromProps,
        //    because the overlay is the most recent dropdown pick
        //    (SOCIAL_TARGET_SELECTED writes here synchronously on
        //    change). bsFromProps mirrors the wizard's stale local
        //    createObjectForm; without this override the row keeps
        //    rendering whatever target the OAuth callback initially
        //    seeded (e.g. "BidLight") even after the user picks a
        //    different destination.
        //
        // We MUST overwrite targetName even when the lookup fails to
        // find a name (dt.name is null) — the dropdown payload may
        // ship name=null. Falling back to the id is still correct, and
        // crucially it does not leave the stale "BidLight" in place.
        if (overlay.selectedTargetId) {
          const dt = (overlay.targets || []).find(t => String(t.id) === String(overlay.selectedTargetId));
          rows[plat].selected.targetId = overlay.selectedTargetId;
          rows[plat].selected.targetName =
            (dt && dt.name) ||
            (dt && dt.id ? String(dt.id) : null) ||
            String(overlay.selectedTargetId);
        }

        if (overlay.selectedAdsId) {
          const da = (overlay.adsAccounts || []).find(a => String(a.id || a.accountId) === String(overlay.selectedAdsId));
          rows[plat].selected.adsId = overlay.selectedAdsId;
          rows[plat].selected.adsName =
            (da && (da.name || da.accountName)) ||
            String(overlay.selectedAdsId);
        }

        // (optional) if you want the row to look “populated” with overlay targets immediately:
        if (Array.isArray(overlay.targets) && overlay.targets.length && rows[plat].targets.length === 0) {
          rows[plat].targets = overlay.targets.map(t => ({
            _id: `${plat}:${t.id}`,            // synthetic id for display
            platform: plat,
            externalId: String(t.id),
            name: t.name,
            type: t.type,
            avatarUrl: t.avatarUrl
          }));
        }
      }

      console.log(`${tag}: platform=${plat}`, {
        accounts: rows[plat].accounts.length,
        targets: rows[plat].targets.length,
        ads: rows[plat].ads.length,
        selected: rows[plat].selected,
        status
      });
    });

    // FINAL OVERRIDE — CREATE MODE ONLY: SMA-local pickedByPlatform
    // wins over everything in the create-brand wizard. In edit mode,
    // skip this override entirely — the user.brands / bsFromProps
    // flow already handles live updates correctly there (and applying
    // the override would freeze stale picks across disconnect/reconnect
    // cycles).
    //
    // `debugKey` carries the explicit isCreating flag we get from
    // addBrandModal (passed in via the hook args). The previous
    // condition `!selectedBrandId && !effectiveBrandSettingsId`
    // failed once `startConnect` minted draft IDs server-side
    // mid-flow — they became truthy and the override stopped firing.
    if (debugKey === 'SMA-create') {
      Object.keys(pickedByPlatform || {}).forEach((plat) => {
        const pick = pickedByPlatform[plat];
        if (!pick || !rows[plat]) return;
        if (pick.targetId) rows[plat].selected.targetId = pick.targetId;
        if (pick.targetName) rows[plat].selected.targetName = pick.targetName;
        else if (pick.targetId) rows[plat].selected.targetName = String(pick.targetId);
        if (pick.adsId) rows[plat].selected.adsId = pick.adsId;
        if (pick.adsName) rows[plat].selected.adsName = pick.adsName;
      });
    }

    console.groupEnd();
    return rows;
  // ⬇️ wiring change: depend on IDs so rows recompute on context switch
  }, [brandSettings, draftsByPlatform, globalConnections, selectedBrandId, effectiveBrandSettingsId, debugKey, pickedByPlatform]);
};

export default function SocialMediaAccounts(props)  {
  // Accept both prop names, plus optional pre-fetched brandSettings from parent.
  const dispatch = useDispatch();
  const {
    _id: _propId,
    brandId: propBrandId,
    brandSettingsId: propBrandSettingsId,
    brandSettings: bsFromProps,
    isCreating: propIsCreating
  } = props;

  const { loading, request, refresh } = useLoadSocialAccounts();
  const [busy, setBusy] = useState(null); // track which platform is in-flight

  // helpful for create-mode: send company to backend when no IDs exist
  const companyId = useSelector((s) => s.user?.userInfo?.company || null);

  // Local authoritative IDs (prop → popup → redux)
  const coerceId = (v) => (v === '' || v == null ? null : v);
  const [localIds, setLocalIds] = useState({
    brandId: coerceId(propBrandId ?? _propId),
    brandSettingsId: coerceId(propBrandSettingsId ?? bsFromProps?._id),
  });

  // Keep localIds in sync if parent updates props
  useEffect(() => {
    setLocalIds(prev => ({
      brandId: (propBrandId ?? _propId) ?? prev.brandId,
      brandSettingsId: (propBrandSettingsId ?? bsFromProps?._id) ?? prev.brandSettingsId,
    }));
  }, [propBrandId, _propId, propBrandSettingsId, bsFromProps?._id]);

  // Pull brand + settings from Redux (to augment/confirm identities, not to force create-mode)
  const reduxPick = useSelector((state) => {
    const brands = state?.user?.brands || [];
    const byBrand = localIds.brandId
      ? brands.find(b => String(b?._id) === String(localIds.brandId))
      : null;
    const bySettings = (!byBrand && localIds.brandSettingsId)
      ? brands.find(b => String(b?.brandSettings?._id) === String(localIds.brandSettingsId))
      : null;
    const selectedBrand = byBrand || bySettings || null;
    return { brand: selectedBrand, brandSettings: selectedBrand?.brandSettings || null };
  });

  // If Redux learns IDs we didn't have (existing brand path), adopt them
  useEffect(() => {
    if (!reduxPick.brand) return;
    setLocalIds(prev => ({
      brandId: prev.brandId ?? reduxPick.brand._id ?? null,
      brandSettingsId: prev.brandSettingsId ?? reduxPick.brand?.brandSettings?._id ?? null,
    }));
  }, [reduxPick.brand, propBrandId, _propId, propBrandSettingsId, bsFromProps?._id]);

  // Determine effective brandSettings.
  //
  // "Create mode" means the user is in the add-brand wizard and has
  // NOT yet committed the brand. The previous condition also required
  // `!bsFromProps`, which broke things: once the OAuth callback
  // populates the wizard's local createObjectForm with byPlatform
  // data, `bsFromProps` becomes truthy and isCreateMode flips false.
  // That disabled the entire draft-slice path below — so the row
  // could never reflect a dropdown change via the overlay, even
  // though we ARE still in the create flow. The right signal is
  // simply "no committed brand id present".
  const isCreateMode = !localIds.brandId && !localIds.brandSettingsId;
  // Original precedence restored: bsFromProps first (it has byPlatform
  // from the OAuth payload that just landed), reduxPick.brandSettings
  // as fallback. Edit mode relies on the standard refresh() →
  // PATCH_BRAND_SETTINGS flow to live-update; the previous "Redux
  // first" attempt broke the edit-mode hydration users relied on.
  const brandSettings =
    !localIds.brandId && !localIds.brandSettingsId && !bsFromProps
      ? null
      : (bsFromProps &&
          (bsFromProps.byPlatform || bsFromProps.socialAccounts || bsFromProps.socialTargets)
          ? bsFromProps
          : reduxPick.brandSettings);

  // Stable primitives
  const selectedBrandId = localIds.brandId ? String(localIds.brandId) : '';
  const effectiveBrandSettingsId = localIds.brandSettingsId || null;

  // AFTER (runs only when the component unmounts)
  useEffect(() => {
    const shouldClear = !selectedBrandId && !effectiveBrandSettingsId; // create-mode?
    return () => {
      if (shouldClear) {
        dispatch({ type: 'SOCIAL_DRAFT_CLEAR', brandSettingsId: 'new' });
        try { sessionStorage.removeItem('social:last'); } catch {}
      }
    };
  }, [selectedBrandId, effectiveBrandSettingsId, dispatch,propBrandId, _propId, propBrandSettingsId, bsFromProps?._id ]);

  // --- DRAFTS: only use drafts in create-mode ---
  // SMC writes the draft slice keyed by `effectiveBrandSettingsId`
  // (which is the real id of the draft brand startConnect minted on
  // the server) when it has one, and falls back to the literal
  // 'new' otherwise. We have to read BOTH keys — last write wins per
  // platform — or the dropdown picks land in drafts[realBsId] while
  // SMA renders from drafts['new'] and the row stays stale forever.
  const draftsByPlatform = useSelector((s) => {
    if (!isCreateMode) return {};
    const all = (s.social && s.social.drafts) || {};
    const byNew = all['new'] || {};
    const byReal = (effectiveBrandSettingsId && all[String(effectiveBrandSettingsId)]) || {};
    const platforms = new Set([...Object.keys(byNew), ...Object.keys(byReal)]);
    const merged = {};
    platforms.forEach((p) => {
      // drafts[realBsId] is the more recent slice (SMC writes there
      // once startConnect mints the brand). Prefer it on the platform
      // entries that exist; for platforms only present in 'new',
      // keep the 'new' draft.
      merged[p] = byReal[p] || byNew[p];
    });
    return merged;
  });

  const globalConnections = useSelector((s) => s.social?.connections || {});

  // SMA-local source of truth for the user's most-recent dropdown
  // pick, populated by the CONNECTION_SAVED postMessage handler. This
  // bypasses every Redux-slice mismatch and React-state staleness
  // issue: a setState here, used by usePlatformRows below, guarantees
  // the row text re-renders.
  const [pickedByPlatform, setPickedByPlatform] = useState({});

  // ⬇️ wiring change: pass IDs into the hook
  // debugKey carries the explicit isCreating flag — usePlatformRows
  // toggles the SMA-local pickedByPlatform override on it. We use
  // debugKey for this dual purpose to avoid adding yet another
  // parameter to an already-long hook signature.
  const rowsByPlatform = usePlatformRows(
    brandSettings,
    draftsByPlatform,
    globalConnections,
    selectedBrandId,
    effectiveBrandSettingsId,
    propIsCreating ? 'SMA-create' : 'SMA',
    pickedByPlatform
  );

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, [propBrandId, _propId, propBrandSettingsId, bsFromProps?._id]);

  // Fetch when we have (brandId || brandSettingsId) OR a full brandSettings object
  useEffect(() => {
    if (selectedBrandId || effectiveBrandSettingsId || brandSettings) {
      console.log('[SMA] request() fetch', { selectedBrandId, effectiveBrandSettingsId, hasBrandSettings: !!brandSettings });
      request();
    }
  }, [selectedBrandId, effectiveBrandSettingsId, brandSettings, request, propBrandId, _propId, propBrandSettingsId, bsFromProps?._id]);

  const openAuthPopup = useCallback(function (url, title) {
    const width = 600, height = 650;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    const win = window.open(
      url,
      title || 'Social Connect',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );
    if (!win || win.closed || typeof win.closed === 'undefined') {
      notify('Please enable popups to continue.', 'danger');
      return null;
    }
    return win;
  }, []);

  const handleConnect = useCallback(async function (platform, { switchAccount = false } = {}) {
    try {
      const payload = {};
      // Force the provider account picker (re-auth) only when the user explicitly
      // wants a DIFFERENT account — keeps routine reconnects silent (no password).
      if (switchAccount) payload.switchAccount = true;
      // startConnect reads ctx.brandId — used to send _id which it ignored,
      // forcing the backend's draft-Untitled-Brand fallback even when the
      // user was on a real brand. Send brandId so the social account
      // actually attaches to the brand they clicked Connect from.
      if (selectedBrandId) payload.brandId = selectedBrandId;
      if (effectiveBrandSettingsId) payload.brandSettingsId = effectiveBrandSettingsId;

      // only in create-mode, include companyId so backend can mint draft brand/settings
      if (isCreateMode && companyId) payload.companyId = companyId;

      const data = await dispatch(startConnect(platform, payload));
      if (data && data.url) openAuthPopup(data.url, 'Connect ' + platform);
    } catch (e) {
      notify((e && e.message) || 'Failed to start connection', 'danger');
    }
  }, [dispatch, openAuthPopup, selectedBrandId, effectiveBrandSettingsId, isCreateMode, companyId]);

  const handleDisconnect = useCallback(async function (platform) {
    try {
      setBusy(platform);
      // Server requires brandId or brandSettingsId to scope the disconnect to
      // one brand's connection (the same platform can be linked to multiple
      // brands). It also revokes server-side at FB/IG/Google/LinkedIn so the
      // next Connect re-prompts with current scopes from scratch.
      await dispatch(
        doDisconnect(platform, {
          brandId: selectedBrandId || undefined,
          brandSettingsId: effectiveBrandSettingsId || undefined,
        })
      );
      notify(platform + ' disconnected.', 'success');
      refresh();
    } catch (e) {
      notify((e && e.message) || 'Failed to disconnect', 'danger');
    } finally {
      setBusy((prev) => (prev === platform ? null : prev));
    }
  }, [dispatch, refresh, selectedBrandId, effectiveBrandSettingsId]);

  // Listen for popup messages (OAuth + Manage)
  useEffect(function () {
    function onMessage(evt) {
      if (!mountedRef.current || !evt || typeof evt.data !== 'object') return;

      const origin = evt.origin || '';
      // Env-driven allowlist: APP_BASE_URL resolves to the same host
      // the bundle was built against (app.prigmar.com in prod,
      // predeployment.prigmar.com in dev — see frontend/src/api/index.js).
      // window.location.origin is included for the same-origin case.
      const allowed = [window.location.origin, APP_BASE_URL];
      if (allowed.indexOf(origin) === -1) return;

      if (evt.data.type === 'AUTH_SUCCESS' && evt.data.data) {
        // Use backend-supplied payload verbatim; adopt canonical IDs if present
        const payload = { ...evt.data.data };
        const { brandId: bid, brandSettingsId: bsid } = payload || {};
        if (bid || bsid) {
          setLocalIds(prev => ({
            brandId: bid  ?? prev.brandId,
            brandSettingsId: bsid ?? prev.brandSettingsId,
          }));
        }
        console.log('[SMA] AUTH_SUCCESS payload → dispatch(applyConnectPayload)', payload);
        dispatch(applyConnectPayload(payload));
        dispatch({
          type: 'SOCIAL_CONNECT_PAYLOAD',
          brandSettingsId: payload.brandSettingsId || (effectiveBrandSettingsId ?? 'new'),
          payload
        });

        refresh();

        // REGRESSION FIX: do NOT clear the draft slice in create mode.
        // The draft slice (social.drafts[<bsid>|new][platform]) IS the
        // source of truth for SMA's row rendering while the wizard is
        // still open — it carries status='connected', accountId,
        // targets, etc. Clearing it immediately after AUTH_SUCCESS
        // wipes the very thing the row reads, so the table only ever
        // looks right after a full page refresh. The
        // ADD_NEW_BRAND/UPSERT_BRAND reducer in social.js already
        // clears the draft when the brand actually commits, so leaving
        // it here is correct.
        //
        // In edit mode (brand already committed) the draft is just a
        // staging buffer — clear it so server-truth in
        // state.user.brands wins.
        if (!isCreateMode) {
          if (payload.brandSettingsId) {
            dispatch({ type: 'SOCIAL_DRAFT_CLEAR', brandSettingsId: payload.brandSettingsId });
          }
          dispatch({ type: 'SOCIAL_DRAFT_CLEAR', brandSettingsId: 'new' });
        }

        try {
          sessionStorage.removeItem('social:last');
          localStorage.removeItem(`social:temp:${payload.platform}`);
        } catch {}
        notify('Connection successful!', 'success');
        try { sessionStorage.removeItem('connect_session_ctx'); } catch (_) {}

      } else if (evt.data.type === 'CONNECTION_SAVED') {
        // Rehydrate Redux immediately from the postMessage payload so the
        // UI reacts to the user's pick without waiting for the async
        // refresh() round-trip (which is cached by useHttpWithCache and
        // can return stale data). The popup ships the new selection in
        // evt.data.data; we dispatch the reducer action that updates
        // brandSettings.selectedByPlatform + byPlatform synchronously.
        //
        // During the create-brand wizard the draft brand is NOT in
        // state.user.brands (startConnect mints it server-side without
        // dispatching ADD_NEW_BRAND), so UPDATE_BRAND_SETTINGS_SELECTION
        // / SET_BRAND_DEFAULT_TARGET below silently no-op. The
        // SOCIAL_TARGET_SELECTED dispatch is the one that ACTUALLY
        // moves the visible state — it writes to
        // social.drafts[brandSettingsId][platform], which usePlatformRows
        // reads as the overlay that wins over bsFromProps. Without
        // this dispatch the row keeps rendering the OAuth-seeded
        // default forever.
        const d = evt.data.data || {};
        const _plat = String(d.platform || '').toLowerCase();
        // FIRST: stash the pick in SMA-local state so the row text
        // re-renders synchronously, completely independent of every
        // Redux slice. If targetName is null/missing, fall back to
        // the id — anything is better than the stale BidLight.
        //
        // Merge target + ads independently so a payload from the
        // target dropdown (which doesn't know which ads is "current")
        // doesn't wipe out a previously-picked ads, and vice versa.
        if (_plat && (d.targetId || d.adsAccountId)) {
          setPickedByPlatform((prev) => {
            const prevPick = prev[_plat] || {};
            return {
              ...prev,
              [_plat]: {
                targetId: d.targetId || prevPick.targetId || null,
                targetName: d.targetName || prevPick.targetName || null,
                adsId: d.adsAccountId || prevPick.adsId || null,
                adsName: d.adsAccountName || prevPick.adsName || null
              }
            };
          });
          // Confirmation toast in the main window (the connect popup may close
          // immediately on Save, killing its own toast before it's seen). Shown
          // here so picking/saving a destination always surfaces a notification.
          if (d.targetId) {
            notify('Publishing destination saved' + (d.targetName ? ': ' + d.targetName : '') + '.', 'info');
          }
        }
        // KEY MISMATCH FIX: in create mode the SMC popup is opened via
        // an OAuth callback URL that carries the draft brand's real
        // brandSettingsId, while addBrandModal hands SMA a null
        // brandSettingsId (the brand isn't committed yet). If we dispatch
        // with d.brandSettingsId verbatim, SMC writes to
        // drafts[realId] but SMA reads from drafts['new']/drafts[null]
        // and the overlay never sees the pick. In create mode we MUST
        // normalize to 'new' on both write and read.
        const _dispatchBsid = isCreateMode
          ? 'new'
          : (d.brandSettingsId || effectiveBrandSettingsId || 'new');
        if (d.platform && d.targetId) {
          dispatch({
            type: 'SOCIAL_TARGET_SELECTED',
            platform: _plat,
            brandSettingsId: _dispatchBsid,
            payload: {
              target: {
                id: d.targetId,
                name: d.targetName || null,
                type: null,
                avatarUrl: null,
                token: null
              },
              adsAccountId: d.adsAccountId || null
            }
          });
        }
        // EDIT-MODE FIX: when SMC's popup is opened from a
        // brand-row's "Manage" button, the callback URL may not
        // carry ?brandId=… so the popup's effectiveBrandId is null
        // and the postMessage ships d.brandId=null. The reducer
        // matches by brandId so a null short-circuits the whole
        // user.brands update path. Fall back to SMA's own
        // selectedBrandId — we know which brand this SMA instance
        // is editing.
        const _brandIdForDispatch = d.brandId || selectedBrandId || null;
        const _bsidForDispatch = d.brandSettingsId || effectiveBrandSettingsId || null;
        if (_brandIdForDispatch && d.platform) {
          dispatch({
            type: 'UPDATE_BRAND_SETTINGS_SELECTION',
            payload: {
              brandId: _brandIdForDispatch,
              brandSettingsId: _bsidForDispatch,
              platform: _plat,
              accountId: d.accountId,
              targetId: d.targetId,
              adsAccountId: d.adsAccountId || null
            }
          });
          // Also update the account.defaultTarget so SMA's resolver
          // (which falls back to account.defaultTarget when no
          // selectedByPlatform.targetId is present) sees the new pick.
          if (d.accountId && d.targetId) {
            dispatch({
              type: 'SET_BRAND_DEFAULT_TARGET',
              payload: {
                brandId: _brandIdForDispatch,
                brandSettingsId: _bsidForDispatch,
                platform: _plat,
                accountId: d.accountId,
                targetId: d.targetId,
                externalTargetId: d.targetId,
                chosenName: d.targetName || null
              }
            });
          }
        }
        console.log('[SMA] CONNECTION_SAVED → rehydrated + refresh()');
        // Don't refresh() on the optimistic phase — the server round-trip
        // would race the dropdown's auto-save and could surface stale
        // data. Refresh only on the server-confirmed save.
        if (!d.optimistic) {
          refresh();
        }
        notify('Target selection saved!', d.optimistic ? 'info' : 'success');

      } else if (evt.data.type === 'AUTH_FAILURE') {
        // evt.data.error may be a RAW provider error object (FB Graph:
        // {message,type,code,fbtrace_id}) — rendering it crashed the page
        // with React #31. Reduce to a string before it touches state/toasts.
        const msg = toMessageString(evt.data.error) || 'Authentication failed';
        console.log('[SMA] AUTH_FAILURE:', msg);
        dispatch(setSocialMessage(msg));
        notify(msg, 'danger');
      }
    }

    window.addEventListener('message', onMessage);

    // Fallback bridge for the dropdown pick: when SMC writes the latest
    // CONNECTION_SAVED payload to localStorage, the 'storage' event
    // fires in this (parent) window. We synthesize a fake postMessage
    // event with the same shape so the existing handler above can
    // process it. This path saves us when window.opener.postMessage
    // drops (cross-origin / opener-closed race / popup security).
    const onStorage = (e) => {
      if (!e || e.key !== 'prigmar:connection_saved' || !e.newValue) return;
      let data;
      try { data = JSON.parse(e.newValue); } catch { return; }
      if (!data || !data.platform) return;
      onMessage({
        origin: window.location.origin,
        data: { type: 'CONNECTION_SAVED', data }
      });
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorage);
    };
  }, [dispatch, refresh, mountedRef, effectiveBrandSettingsId, propBrandId, _propId, propBrandSettingsId, bsFromProps?._id]);

  // shared style to keep the single action button in the same place/size
  const actionBtnStyle = {
    fontSize: '0.975rem',
    padding: '0.2rem 0.65rem',
    lineHeight: 1.2,
    minHeight: 13,
    minWidth: 120, // keep consistent width so layout doesn't jump
    boxShadow: '2.6px 2.6px lightgrey'
  };

  return (
    <MDBContainer className="py-5">
      <MDBCard>
        <MDBCardHeader className="bg-light">
          <h4 className="text-center mb-3">Publishing to your Social Accounts with Prigmar</h4>
          <p className="text-muted text-center small mb-0">
            You can connect one profile for each social media platform (i.e. Facebook, Twitter, Instagram, etc).
            To manage multiple profiles for the same network, create another account and publish to multiple.
          </p>
        </MDBCardHeader>

        <MDBCardBody>
          <MDBTable hover responsive className="align-middle">
            <MDBTableHead color="primary-color" textWhite>
              <tr>
                <th style={{ fontWeight: 'bold', fontSize: '1.14rem', padding: '0.65rem' }}>Social Accounts</th>
                <th style={{ fontWeight: 'bold', fontSize: '1.14rem', padding: '0.65rem' }}>Status</th>
                <th style={{ fontWeight: 'bold', fontSize: '1.14rem', padding: '0.65rem' }}>Actions</th>
              </tr>
            </MDBTableHead>
            <MDBTableBody>
              {accountsMeta.map(function (meta) {
                const plat = meta.platform;
                const row  = rowsByPlatform?.[plat] || null;
                const status = row ? (row.status || 'disconnected') : 'disconnected';

                const subtitleParts = [];
                if (row?.selected?.targetName) subtitleParts.push('Target: ' + row.selected.targetName);
                if (row?.selected?.adsName)    subtitleParts.push('Ads: ' + row.selected.adsName);
                const subtitle = subtitleParts.join(' • ');
                const targetSourceUrl = row?.selected?.targetName
                  ? getTargetSourceUrl(plat, row.selected.targetType, row.selected.targetExternalId, row.selected.targetName, row.selected.socialHandle)
                  : null;

                const statusReason = row ? row.statusReason : null;
                const isConnected = status === 'connected';
                const btn = connectButtonFor(status);
                const btnColor = btn.color;
                const btnLabel = btn.label;
                const showReason = shouldShowStatusReason(status, statusReason);
                const reasonTip = statusReasonTooltip(status, statusReason);

                return (
                  <tr key={plat}>
                    <td style={{ padding: '0.65rem' }}>
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle mr-3"
                          style={{
                            backgroundColor: 'var(--' + meta.color + ')',
                            padding: '0.45rem',
                            width: 31, height: 31, display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}
                        >
                          <i className={meta.icon + ' text-white'} style={{ fontSize: '0.975rem' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: '1.14rem' }}>{meta.name}</div>
                          {(row?.selected?.targetName || row?.selected?.adsName) ? (
                            <small className="text-muted" style={{ display: 'block', lineHeight: 1.5 }}>
                              {row?.selected?.targetName ? (
                                targetSourceUrl ? (
                                  <span>Target:{' '}
                                    <a href={targetSourceUrl} target="_blank" rel="noopener noreferrer"
                                      style={{ color: '#0b3d91', fontWeight: 500 }}
                                      title={'Open ' + (row.selected.targetName || 'target') + ' on ' + meta.name}>
                                      {row.selected.targetName}{' '}
                                      <i className="fas fa-external-link-alt" style={{ fontSize: '0.6rem' }} />
                                    </a>
                                  </span>
                                ) : (
                                  <span>Target: {row.selected.targetName}</span>
                                )
                              ) : null}
                              {row?.selected?.targetName && row?.selected?.adsName ? ' • ' : null}
                              {row?.selected?.adsName ? <span>Ads: {row.selected.adsName}</span> : null}
                            </small>
                          ) : null}
                        </div>
                      </div>
                    </td>

                    <td style={{ padding: '0.65rem' }}>
                      <span
                        style={{
                          fontSize: '0.975rem',
                          padding: '0.2rem 0.65rem',
                          borderRadius: '1.3rem',
                          backgroundColor:
                            status === 'connected' ? '#e8f5e9' :
                            status === 'expired'   ? '#fff8e1' :
                            status === 'failed'    ? '#ffebee'  : '#f5f5f5',
                          color:
                            status === 'connected' ? '#2e7d32' :
                            status === 'expired'   ? '#ed6c02' :
                            status === 'failed'    ? '#c62828'  : '#616161'
                        }}
                      >
                        {status}
                      </span>

                      {showReason ? (
                        <MDBTooltip placement="top" domElement tag="span" className="ml-2">
                          <span
                            className="d-inline-block"
                            tabIndex="0"
                            style={{ cursor: 'help' }}
                            aria-label="Why is this account not working?"
                          >
                            <i
                              className="fas fa-question-circle"
                              style={{ fontSize: '0.975rem', color: '#c62828' }}
                            />
                          </span>
                          <span style={{ fontSize: '0.9rem' }}>{reasonTip}</span>
                        </MDBTooltip>
                      ) : null}
                    </td>

                    <td style={{ padding: '0.65rem' }}>
                      <MDBBtn
                        color={btnColor}
                        size="sm"
                        className="m-0"
                        style={actionBtnStyle}
                        disabled={busy === plat}
                        onClick={() =>
                          isConnected
                            ? handleDisconnect(plat)
                            : handleConnect(plat)
                        }
                      >
                        {busy === plat ? 'Working…' : btnLabel}
                      </MDBBtn>

                      {meta.hasHelp ? (
                        <MDBTooltip placement="top" domElement tag="span" className="ml-2">
                          <span className="d-inline-block" tabIndex="0" style={{ cursor: 'help' }}>
                            <i className="fas fa-question-circle text-muted" style={{ fontSize: '0.975rem' }} />
                          </span>
                          <span style={{ fontSize: '0.975rem' }}>
                            Get help with custom channels
                          </span>
                        </MDBTooltip>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </MDBTableBody>
          </MDBTable>
        </MDBCardBody>
      </MDBCard>
    </MDBContainer>
  );
}
